// src/components/SignupPage.js - Enhanced with referral link handling
import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Divider,
  StepConnector,
  Avatar,
  useTheme,
  useMediaQuery,
  Chip,
  Card,
  CardContent,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Google as GoogleIcon,
  AccountCircle,
  Phone,
  CardGiftcard,
  ArrowBack,
  Check,
  Person,
  PersonAdd,
  Handshake,
} from "@mui/icons-material";
import {
  signInWithPopup,
  onAuthStateChanged,
  signOut,
  GoogleAuthProvider,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { auth, googleProvider, db } from "../firebase/config";
import { validateForm, signupValidationSchema, ValidationError } from "../utils/validation";
import { fixSpecificUserReferralId, fixMissingReferralIds } from "../utils/fixReferralIds";

import PublicHomePage from "./PublicHomePage";
import HomePage from "./HomePage";
import AdminGuard from "./AdminGuard";
import AdminDashboard from "./AdminDashboard";
import { brandPalette as primaryBlue } from "../config/brandTheme";

const steps = ["Google Login", "Personal Info", "Referral Code"];

// Custom styled stepper connector
const CustomConnector = styled(StepConnector)(({ theme }) => ({
  [`&.MuiStepConnector-root`]: {
    top: 10,
  },
  [`&.Mui-active`]: {
    [`& .MuiStepConnector-line`]: {
      backgroundImage: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
    },
  },
  [`&.Mui-completed`]: {
    [`& .MuiStepConnector-line`]: {
      backgroundImage: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
    },
  },
  [`& .MuiStepConnector-line`]: {
    height: 3,
    border: 0,
    backgroundColor:
      theme.palette.mode === "dark" ? theme.palette.grey[800] : "#eaeaf0",
    borderRadius: 1,
  },
}));

// Custom styled step icon
const CustomStepIcon = styled("div")(({ theme, ownerState }) => ({
  backgroundColor:
    theme.palette.mode === "dark" ? theme.palette.grey[700] : "#ccc",
  zIndex: 1,
  color: "#fff",
  width: 24,
  height: 24,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  ...(ownerState.active && {
    backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
    boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
  }),
  ...(ownerState.completed && {
    backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
  }),
}));

const addFirstTimeCashback = async (userId) => {
  try {
    // Find user document
    const userQuery = query(
      collection(db, "users"),
      where("uid", "==", userId)
    );
    const userSnapshot = await getDocs(userQuery);

    if (!userSnapshot.empty) {
      const userDocRef = doc(db, "users", userSnapshot.docs[0].id);
      const userData = userSnapshot.docs[0].data();

      // Check if this is first package purchase and cashback not already given
      if (userData.hasPurchasedPackage && userData.cashbackEarned === 0) {
        await updateDoc(userDocRef, {
          cashbackBalance: 499,
          cashbackEarned: 499,
          updatedAt: new Date().toISOString(),
        });

        console.log(
          `Added ₹499 cashback to user ${userId} for first package purchase`
        );
        return true;
      }
    }
    return false;
  } catch (error) {
    console.error("Error adding first-time cashback:", error);
    throw error;
  }
};

const updateCommissionBalance = async (
  userId,
  commissionAmount,
  operation = "add"
) => {
  try {
    const userQuery = query(
      collection(db, "users"),
      where("uid", "==", userId)
    );
    const userSnapshot = await getDocs(userQuery);

    if (!userSnapshot.empty) {
      const userDocRef = doc(db, "users", userSnapshot.docs[0].id);
      const userData = userSnapshot.docs[0].data();

      const currentBalance = userData.commissionBalance || 0;
      const currentEarned = userData.commissionEarned || 0;
      const currentUsed = userData.commissionUsed || 0;

      let updateData = {};

      if (operation === "add") {
        updateData = {
          commissionBalance: currentBalance + commissionAmount,
          commissionEarned: currentEarned + commissionAmount,
          updatedAt: new Date().toISOString(),
        };
      } else if (operation === "deduct") {
        updateData = {
          commissionBalance: Math.max(0, currentBalance - commissionAmount),
          commissionUsed: currentUsed + commissionAmount,
          updatedAt: new Date().toISOString(),
        };
      }

      await updateDoc(userDocRef, updateData);
      return true;
    }
    return false;
  } catch (error) {
    console.error("Error updating commission balance:", error);
    throw error;
  }
};

const updateCashbackBalance = async (
  userId,
  cashbackAmount,
  operation = "deduct"
) => {
  try {
    const userQuery = query(
      collection(db, "users"),
      where("uid", "==", userId)
    );
    const userSnapshot = await getDocs(userQuery);

    if (!userSnapshot.empty) {
      const userDocRef = doc(db, "users", userSnapshot.docs[0].id);
      const userData = userSnapshot.docs[0].data();

      const currentBalance = userData.cashbackBalance || 0;
      const currentUsed = userData.cashbackUsed || 0;

      if (operation === "deduct") {
        const updateData = {
          cashbackBalance: Math.max(0, currentBalance - cashbackAmount),
          cashbackUsed: currentUsed + cashbackAmount,
          updatedAt: new Date().toISOString(),
        };

        await updateDoc(userDocRef, updateData);
        return true;
      }
    }
    return false;
  } catch (error) {
    console.error("Error updating cashback balance:", error);
    throw error;
  }
};

const debugReferralValidation = async (referralId) => {
  console.log("=== REFERRAL DEBUG START ===");
  console.log("Input referral ID:", referralId);
  console.log("Type:", typeof referralId);
  console.log("Length:", referralId?.length);

  try {
    // Check if we can query the users collection at all
    const allUsersQuery = query(collection(db, "users"));
    const allUsersSnapshot = await getDocs(allUsersQuery);

    console.log("Total users in database:", allUsersSnapshot.docs.length);

    // Log all users and their referral IDs
    console.log("All users with their referral IDs:");
    allUsersSnapshot.docs.forEach((doc) => {
      const userData = doc.data();
      console.log(
        `- ${userData.name} (${userData.email}): ${userData.myReferralId}`
      );
    });

    // Now try the specific query
    console.log("\n--- Trying specific query ---");
    const specificQuery = query(
      collection(db, "users"),
      where("myReferralId", "==", referralId)
    );

    const specificSnapshot = await getDocs(specificQuery);
    console.log("Specific query results:", specificSnapshot.docs.length);

    if (specificSnapshot.docs.length > 0) {
      const userData = specificSnapshot.docs[0].data();
      console.log("Found user:", userData.name, userData.email);
      return userData;
    } else {
      console.log("No user found with referral ID:", referralId);

      // Try case-insensitive search
      console.log("\n--- Trying case-insensitive search ---");
      allUsersSnapshot.docs.forEach((doc) => {
        const userData = doc.data();
        if (
          userData.myReferralId?.toLowerCase() === referralId?.toLowerCase()
        ) {
          console.log(
            "Found case mismatch:",
            userData.myReferralId,
            "vs",
            referralId
          );
        }
      });

      return null;
    }
  } catch (error) {
    console.error("Debug query error:", error);
    return null;
  }
};

const calculateAvailableDiscounts = (userProfile) => {
  const cashbackBalance = userProfile?.cashbackBalance || 0;
  const commissionBalance = userProfile?.commissionBalance || 0;

  // Cart checkout caps cashback at profit margin on eligible lines (see CartPage)
  const availableCashbackDiscount = cashbackBalance;

  // Commission can be used fully but with 10% admin fee
  const availableCommissionDiscount = commissionBalance;
  const commissionAdminFee = commissionBalance * 0.1;
  const netCommissionDiscount = commissionBalance - commissionAdminFee;

  return {
    cashback: {
      balance: cashbackBalance,
      availableDiscount: availableCashbackDiscount,
      percentage: null,
    },
    commission: {
      balance: commissionBalance,
      availableDiscount: netCommissionDiscount, // After 10% admin fee
      adminFee: commissionAdminFee,
      netAmount: netCommissionDiscount,
    },
  };
};

const SignupPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeStep, setActiveStep] = useState(0);
  const [user, setUser] = useState(null);
  const [userExists, setUserExists] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [currentFlow, setCurrentFlow] = useState("public"); // 'public', 'landing', 'signup', 'signin', 'admin', 'home'
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    address: "",
    pincode: "",
    referralCode: "",
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [signupComplete, setSignupComplete] = useState(false);
  const [urlReferralId, setUrlReferralId] = useState("");

  // NEW: Add referrer information state
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [referrerLoading, setReferrerLoading] = useState(false);

  // Glassomorphism styles similar to HomePage
  const glassStyles = {
    background: "rgba(255, 255, 255, 0.25)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.37)",
  };

  useEffect(() => {
    console.log("=== STATE CHANGE DEBUG ===");
    console.log("currentFlow:", currentFlow);
    console.log("user:", user?.email);
    console.log("userExists:", userExists);
    console.log("signupComplete:", signupComplete);
    console.log("urlReferralId:", urlReferralId);
    console.log("referrerInfo:", referrerInfo?.name);
    console.log("authLoading:", authLoading);
    console.log("activeStep:", activeStep);
    console.log("========================");
  }); // No dependency array to track all changes


  // NEW: Function to fetch referrer information
  const fetchReferrerInfo = async (referralId) => {
    try {
      setReferrerLoading(true);
      console.log("🔍 DEBUG: Fetching referrer info for ID:", referralId);
      
      const q = query(
        collection(db, "users"),
        where("myReferralId", "==", referralId.toUpperCase())
      );
      const querySnapshot = await getDocs(q);
      console.log("🔍 DEBUG: Query result count:", querySnapshot.docs.length);

      if (!querySnapshot.empty) {
        const referrerDoc = querySnapshot.docs[0];
        const referrerData = referrerDoc.data();
        console.log("✅ DEBUG: Found referrer:", referrerData.name, referrerData.myReferralId);
        
        setReferrerInfo({
          id: referrerDoc.id,
          name: referrerData.name,
          email: referrerData.email,
          myReferralId: referrerData.myReferralId,
          photoURL: referrerData.photoURL,
          createdAt: referrerData.createdAt,
        });
        return true;
      } else {
        console.log("❌ DEBUG: No referrer found for ID:", referralId.toUpperCase());
        
        // Additional debugging: Check if there are any users with similar IDs
        console.log("🔍 DEBUG: Checking for similar referral IDs...");
        const allUsersQuery = query(collection(db, "users"));
        const allUsersSnapshot = await getDocs(allUsersQuery);
        
        const allReferralIds = [];
        allUsersSnapshot.docs.forEach((doc) => {
          const userData = doc.data();
          if (userData.myReferralId) {
            allReferralIds.push(userData.myReferralId);
            
            // Check for potential matches
            if (userData.myReferralId.toLowerCase() === referralId.toLowerCase()) {
              console.log("🔍 DEBUG: Found case mismatch:", userData.myReferralId, "vs", referralId);
            }
          }
        });
        
        console.log("🔍 DEBUG: All available referral IDs:", allReferralIds.slice(0, 10)); // Show first 10
        console.log("🔍 DEBUG: Total users with referral IDs:", allReferralIds.length);
        
        setReferrerInfo(null);
        return false;
      }
    } catch (error) {
      console.error("❌ DEBUG: Error fetching referrer info:", error);
      setReferrerInfo(null);
      return false;
    } finally {
      setReferrerLoading(false);
    }
  };

  // TEMPORARY: Admin function to check if user exists (for debugging)
  window.checkUserExists = async (email) => {
    try {
      console.log(`🔍 Checking if ${email} is registered...`);
      
      const q = query(
        collection(db, "users"),
        where("email", "==", email.toLowerCase())
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        console.log(`✅ EMAIL NOT REGISTERED: ${email}`);
        console.log(`👍 Safe to share referral link - this is a new user!`);
        return { exists: false, userData: null };
      } else {
        const userData = querySnapshot.docs[0].data();
        console.log(`❌ EMAIL ALREADY REGISTERED: ${email}`);
        console.log(`📋 User Info:`, {
          name: userData.name,
          email: userData.email,
          registeredAt: userData.createdAt,
          hasReferralId: !!userData.myReferralId,
          myReferralId: userData.myReferralId,
          referralUsed: userData.referralIdUsed || 'None'
        });
        console.log(`⚠️  Do not share referral link - user already exists!`);
        return { exists: true, userData: userData };
      }
    } catch (error) {
      console.error('❌ Error checking user:', error);
      return { error: error.message };
    }
  };

  // TEMPORARY: Function to debug specific referral ID
  window.debugReferralId = async (referralId) => {
    try {
      console.log(`🔍 DEBUG: Investigating referral ID: ${referralId}`);
      
      // Check exact match
      const exactQuery = query(
        collection(db, "users"),
        where("myReferralId", "==", referralId.toUpperCase())
      );
      const exactResult = await getDocs(exactQuery);
      console.log(`🔍 Exact match results for "${referralId.toUpperCase()}":`, exactResult.docs.length);
      
      // Check all users and their referral IDs
      console.log(`🔍 Getting all users to check referral IDs...`);
      const allUsersQuery = query(collection(db, "users"));
      const allUsers = await getDocs(allUsersQuery);
      
      const referralData = [];
      allUsers.docs.forEach((doc) => {
        const userData = doc.data();
        if (userData.myReferralId) {
          referralData.push({
            email: userData.email,
            name: userData.name,
            myReferralId: userData.myReferralId,
            docId: doc.id
          });
          
          // Check for potential matches
          if (userData.myReferralId === referralId.toUpperCase()) {
            console.log(`✅ EXACT MATCH FOUND:`, {
              email: userData.email,
              name: userData.name,
              myReferralId: userData.myReferralId,
              docId: doc.id
            });
          }
          
          if (userData.myReferralId.toLowerCase() === referralId.toLowerCase()) {
            console.log(`🔍 Case-insensitive match:`, userData.myReferralId, 'vs', referralId);
          }
          
          if (userData.myReferralId.includes(referralId) || referralId.includes(userData.myReferralId)) {
            console.log(`🔍 Partial match:`, userData.myReferralId, 'vs', referralId);
          }
        }
      });
      
      console.log(`🔍 Total users with referral IDs:`, referralData.length);
      console.log(`🔍 First 10 referral IDs:`, referralData.slice(0, 10).map(u => ({email: u.email, id: u.myReferralId})));
      
      // Specifically check for the user gokkulkumaradobe@gmail.com
      const specificUser = referralData.find(u => u.email === 'gokkulkumaradobe@gmail.com');
      if (specificUser) {
        console.log(`🎯 Found gokkulkumaradobe@gmail.com:`, specificUser);
        
        if (specificUser.myReferralId === referralId.toUpperCase()) {
          console.log(`✅ PERFECT MATCH! Your referral ID is correct!`);
        } else {
          console.log(`❌ MISMATCH! Expected: ${referralId}, Found: ${specificUser.myReferralId}`);
        }
      } else {
        console.log(`❌ User gokkulkumaradobe@gmail.com not found in database!`);
      }
      
      return { referralData, exactMatch: exactResult.docs.length > 0, specificUser };
    } catch (error) {
      console.error('❌ Error debugging referral ID:', error);
      return { error: error.message };
    }
  };

  // TEMPORARY: Function to check specific user's actual referral ID
  window.checkUserReferralId = async (email) => {
    try {
      console.log(`🔍 Checking actual referral ID for: ${email}`);
      
      const userQuery = query(
        collection(db, "users"),
        where("email", "==", email.toLowerCase())
      );
      const userResult = await getDocs(userQuery);
      
      if (userResult.empty) {
        console.log(`❌ User not found: ${email}`);
        return { found: false };
      }
      
      const userData = userResult.docs[0].data();
      console.log(`✅ User found:`, {
        email: userData.email,
        name: userData.name,
        myReferralId: userData.myReferralId || 'NOT SET',
        uid: userData.uid,
        createdAt: userData.createdAt
      });
      
      if (!userData.myReferralId) {
        console.log(`⚠️ WARNING: User ${email} does not have a myReferralId field!`);
        console.log(`🔧 This user needs to have a referral ID generated.`);
      }
      
      return { 
        found: true, 
        userData: userData,
        hasReferralId: !!userData.myReferralId 
      };
    } catch (error) {
      console.error('❌ Error checking user referral ID:', error);
      return { error: error.message };
    }
  };

  // TEMPORARY: Function to investigate specific referral problem
  window.investigateReferral = async (email, expectedReferralId) => {
    try {
      console.log(`🔍 INVESTIGATING REFERRAL PROBLEM`);
      console.log(`📧 Email: ${email}`);
      console.log(`🎯 Expected Referral ID: ${expectedReferralId}`);
      console.log(`================================`);
      
      // 1. Check if user exists and their current referral ID
      console.log(`\n1️⃣ CHECKING USER DATA:`);
      const userResult = await window.checkUserReferralId(email);
      
      if (!userResult.found) {
        console.log(`❌ PROBLEM: User not found in database!`);
        return { issue: 'user_not_found' };
      }
      
      const userData = userResult.userData;
      const currentReferralId = userData.myReferralId;
      
      // 2. Compare expected vs actual
      console.log(`\n2️⃣ COMPARISON:`);
      if (currentReferralId === expectedReferralId) {
        console.log(`✅ MATCH: Current referral ID matches expected!`);
        console.log(`🔍 Let's check if the referral validation is working...`);
        
        // Test the validation function
        const testResult = await window.debugReferralId(expectedReferralId);
        if (testResult.exactMatch) {
          console.log(`✅ VALIDATION WORKS: The referral ID is found in database`);
          console.log(`🤔 Issue might be with frontend logic or URL handling`);
        } else {
          console.log(`❌ VALIDATION FAILS: Referral ID not found despite being set`);
          console.log(`🐛 This suggests a database query issue`);
        }
        
      } else {
        console.log(`❌ MISMATCH!`);
        console.log(`   Expected: ${expectedReferralId}`);
        console.log(`   Current:  ${currentReferralId || 'NOT SET'}`);
        
        if (!currentReferralId) {
          console.log(`🔧 SOLUTION: User needs referral ID generated`);
          return { 
            issue: 'missing_referral_id', 
            userData: userData,
            expectedId: expectedReferralId 
          };
        } else {
          console.log(`🔧 SOLUTION: User's referral ID needs to be corrected`);
          return { 
            issue: 'incorrect_referral_id', 
            userData: userData,
            currentId: currentReferralId,
            expectedId: expectedReferralId 
          };
        }
      }
      
      return { issue: 'validation_logic_problem', userData: userData };
      
    } catch (error) {
      console.error('❌ Error investigating referral:', error);
      return { error: error.message };
    }
  };

  // TEMPORARY: Make fix functions available globally
  window.fixUserReferralId = fixSpecificUserReferralId;
  window.fixAllMissingReferralIds = fixMissingReferralIds;
  
  // TEMPORARY: Quick fix function for the specific issue
  window.fixInfinityWhaleUser = async () => {
    console.log("🚀 Quick fix for infinitywhale08@gmail.com user...");
    const result = await fixSpecificUserReferralId('infinitywhale08@gmail.com', '5A6SHECM');
    
    if (result.success) {
      console.log("✅ FIXED! Now test the referral link: https://infinitywhale.com/signup?ref=5A6SHECM");
    } else {
      console.log("❌ Fix failed:", result.error);
    }
    
    return result;
  };

  // Extract referral ID from URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refParam = urlParams.get("ref");

    if (refParam) {
      console.log("Referral ID found in URL:", refParam);
      setUrlReferralId(refParam.toUpperCase());
      setFormData((prev) => ({
        ...prev,
        referralId: refParam.toUpperCase(),
      }));

      // NEW: Fetch referrer information
      fetchReferrerInfo(refParam);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("🔥 AUTH CHANGED - User:", user?.email || "null");

      // NEW: Check for auth intent from URL
      const urlParams = new URLSearchParams(window.location.search);
      const authIntent = urlParams.get("auth_intent");
      const itemId = urlParams.get("item_id");

      if (user) {
        console.log("📋 Checking if user exists in Firestore...");

        // Check if user exists in Firestore
        const userDoc = await getDoc(doc(db, "users", user.uid));
        console.log("📄 User document exists:", userDoc.exists());

        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log("✅ EXISTING USER FOUND:", userData.name);
          console.log("   ReferralIdUsed:", userData.referralIdUsed);

          setUser(user);
          setUserExists(true);
          setSignupComplete(true);

          // CHECK: If this user just completed signup in this session, go to home
          if (signupComplete && currentFlow === "signup") {
            console.log("🎉 User just completed signup - going to home");
            setCurrentFlow("home");
            return;
          }

          // NEW: Handle auth intent for existing users
          if (authIntent === "add_to_cart" && itemId) {
            console.log(
              "🛒 Existing user with add_to_cart intent - going to home"
            );
            setSuccess(
              "Welcome back! Your selected item will be added to your cart shortly."
            );
            setCurrentFlow("home");
            return;
          }

          // Handle existing user with referral link (only if they didn't just sign up)
          if (urlReferralId && currentFlow !== "home") {
            console.log("🔗 Existing user with referral link detected");

            // If they used the SAME referral they're clicking, just go to home
            if (userData.referralIdUsed === urlReferralId) {
              console.log(
                "✅ User clicking their own successful referral - going to home"
              );
              setSuccess(
                `Welcome back ${userData.name}! You successfully joined via this referral.`
              );
              setCurrentFlow("home");
              return;
            }

            if (!userData.referralIdUsed) {
              console.log("⚠️ User never used referral before");
              setError(
                `Welcome back ${
                  userData.name || "User"
                }! You're already registered. Referral codes can only be used during first-time signup.`
              );
            } else {
              console.log(
                "⚠️ User already used different referral:",
                userData.referralIdUsed
              );
              setError(
                `Welcome back ${
                  userData.name || "User"
                }! You already joined using referral code: ${
                  userData.referralIdUsed
                }`
              );
            }
            console.log("🔄 Setting flow to existing-user-referral");
            setCurrentFlow("existing-user-referral");
          } else {
            console.log("🏠 Normal existing user flow");
            if (currentFlow === "public") {
              setCurrentFlow("home");
            }
          }
        } else {
          console.log("🆕 NEW USER - Starting signup flow");
          console.log("   User UID:", user.uid);
          console.log("   User email:", user.email);

          setUser(user);
          setUserExists(false);
          setSignupComplete(false);

          // Pre-fill form data including referral ID if available
          setFormData((prev) => ({
            ...prev,
            fullName: user.displayName || "",
            email: user.email || "",
            referralCode: urlReferralId || prev.referralCode,
          }));

          // NEW: If they came from auth intent (shared item), show appropriate message
          if (authIntent === "add_to_cart" && itemId) {
            console.log(
              "🔗 New user with add_to_cart intent - going to signup"
            );
            setCurrentFlow("signup");
            setActiveStep(1);
            setSuccess(
              "Welcome! Complete your profile setup and your selected item will be added to your cart."
            );
          }
          // If they came from referral link, automatically start signup flow
          else if (urlReferralId) {
            console.log("🔗 New user with referral - going to signup");
            setCurrentFlow("signup");
            setActiveStep(1);
            setSuccess(
              `Welcome! Complete your profile to join via ${
                referrerInfo?.name || "referral link"
              }.`
            );
          } else if (currentFlow === "home" || currentFlow === "admin") {
            console.log("🔗 New user without referral - going to signup");
            setCurrentFlow("signup");
            setActiveStep(1);
            setSuccess("Please complete your profile setup to continue.");
          }
        }
      } else {
        console.log("❌ No user authenticated");
        setUser(null);
        setUserExists(false);
        setSignupComplete(false);

        // NEW: Handle auth intent for non-authenticated users
        if (authIntent === "add_to_cart" && itemId) {
          console.log(
            "🔗 No user but auth intent exists - showing landing for auth"
          );
          setCurrentFlow("landing");
          setSuccess(
            "Please sign in to add this item to your cart. The item will be automatically added after authentication."
          );
        }
        // Handle referral without auth
        else if (urlReferralId && currentFlow === "public") {
          console.log(
            "🔗 No user but referral exists - showing referral signup"
          );
          setCurrentFlow("referral-signup");
        }
      }
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, [currentFlow, urlReferralId, referrerInfo, signupComplete]);

  // Add this to track when the component renders different flows
  useEffect(() => {
    console.log("🖼️ RENDER FLOW:", currentFlow);
    if (currentFlow === "existing-user-referral") {
      console.log("🔴 SHOWING EXISTING USER REFERRAL PAGE");
    }
  }, [currentFlow]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authIntent = urlParams.get("auth_intent");
    const itemId = urlParams.get("item_id");

    if (authIntent === "add_to_cart" && itemId) {
      console.log(
        "🔍 AUTH INTENT DETECTED - User needs to sign in to add item to cart"
      );
      console.log("Item ID:", itemId);

      // Force authentication flow instead of showing public page
      if (!user) {
        console.log("🔄 No user authenticated - showing landing page for auth");
        setCurrentFlow("landing");

        // Set a success message about the pending item
        setSuccess(
          "Please sign in to add this item to your cart. The item will be automatically added after authentication."
        );
      }
    }
  }, [user]); // Run when user state changes

  // Generate unique referral ID
  const generateReferralId = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let result = "";
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // Check if referral ID is unique
  const isReferralIdUnique = async (referralId) => {
    const q = query(
      collection(db, "users"),
      where("myReferralId", "==", referralId)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.empty;
  };

  // Generate unique referral ID
  const generateUniqueReferralId = async () => {
    let referralId;
    let isUnique = false;

    while (!isUnique) {
      referralId = generateReferralId();
      isUnique = await isReferralIdUnique(referralId);
    }

    return referralId;
  };

  // Handle auth requirement (called from public page when user tries to purchase)
  const handleAuthRequired = () => {
    setCurrentFlow("landing");
  };

  // Handle Google Authentication (Smart routing)
  const handleGoogleAuth = async (intendedFlow) => {
    try {
      setLoading(true);
      setError("");

      // Create a fresh provider instance with forced account selection
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: "select_account", // Force account selection every time
      });

      // Use the provider with forced account selection
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Check if user already exists
      const userDoc = await getDoc(doc(db, "users", user.uid));

      if (userDoc.exists()) {
        // User exists
        const userData = userDoc.data();

        // NEW: Handle existing user with referral link
        if (urlReferralId) {
          if (!userData.referralIdUsed) {
            setError(
              `Welcome back ${
                userData.name || "User"
              }! You're already registered. Referral codes can only be used during first-time signup.`
            );
          } else {
            setError(
              `Welcome back ${
                userData.name || "User"
              }! You already joined using referral code: ${
                userData.referralIdUsed
              }`
            );
          }
          setCurrentFlow("existing-user-referral");
        } else {
          if (intendedFlow === "signup") {
            setError(
              "This account already exists. You have been signed in instead."
            );
          } else {
            setSuccess("Welcome back! You are now signed in.");
          }
          setCurrentFlow("home");
        }

        setUser(user);
        setUserExists(true);
        setSignupComplete(true);
      } else {
        // New user
        if (intendedFlow === "signin") {
          setError(
            "No account found for this Google account. Please sign up first."
          );
          setCurrentFlow("signup");
          setActiveStep(0);
        } else {
          setUser(user);
          setUserExists(false);
          setFormData((prev) => ({
            ...prev,
            fullName: user.displayName || "",
            email: user.email || "",
          }));
          setCurrentFlow("signup");
          setActiveStep(1);

          // NEW: Customize success message based on referral
          if (urlReferralId && referrerInfo) {
            setSuccess(
              `Successfully signed in with Google! Complete your profile to join via ${referrerInfo.name}'s referral.`
            );
          } else {
            setSuccess(
              "Successfully signed in with Google! Please complete your profile."
            );
          }
        }
      }
    } catch (error) {
      setError("Failed to authenticate with Google: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes with validation
  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Handle personal info submission
  const handlePersonalInfoSubmit = () => {
    if (!formData.fullName.trim() || !formData.phone.trim() || !formData.email.trim() || !formData.address.trim() || !formData.pincode.trim()) {
      setError("Please fill in all required fields");
      return;
    }

    // Basic phone number validation
    const phoneRegex = /^\+?[\d\s-()]{10,}$/;
    if (!phoneRegex.test(formData.phone)) {
      setError("Please enter a valid phone number");
      return;
    }

    setError("");

    // NEW: If referral ID is already set from URL, skip to final signup
    if (urlReferralId) {
      handleFinalSignup();
    } else {
      setActiveStep(2);
    }
  };

  // Validate referral ID
  const validateReferralId = async (referralId) => {
    if (!referralId.trim()) return { isValid: true, referrerData: null };

    const q = query(
      collection(db, "users"),
      where("myReferralId", "==", referralId.toUpperCase())
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return { isValid: false, referrerData: null };
    }

    const referrerDoc = querySnapshot.docs[0];
    return {
      isValid: true,
      referrerData: { id: referrerDoc.id, ...referrerDoc.data() },
    };
  };

  // Handle final signup
  const handleFinalSignup = async () => {
    try {
      setLoading(true);
      setError("");
      setValidationErrors({});

      // Validate form data
      const validation = validateForm({
        fullName: formData.fullName || user?.displayName || "",
        email: formData.email || user?.email || "",
        phone: formData.phone,
        address: formData.address,
        pincode: formData.pincode,
        referralCode: formData.referralCode
      }, signupValidationSchema);

      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        setError("Please correct the errors in the form");
        setLoading(false);
        return;
      }

      // Use sanitized data from validation
      const sanitizedData = validation.sanitizedData;

      // Generate unique referral ID for this user
      const myReferralId = await generateUniqueReferralId();

      // Validate referral ID if provided, otherwise use default (infinitywhale08@gmail.com)
      const DEFAULT_REFERRAL_ID = "5A6SHECM"; // infinitywhale08@gmail.com
      let referredBy = "company";
      let referrerData = null;
      let usedReferralCode = sanitizedData.referralCode.trim();

      // If no referral code provided, use default
      if (!usedReferralCode) {
        usedReferralCode = DEFAULT_REFERRAL_ID;
        console.log("No referral code provided, using default:", DEFAULT_REFERRAL_ID);
      }

      // Validate the referral ID (either user-provided or default)
      const referralValidation = await validateReferralId(usedReferralCode);
      if (!referralValidation.isValid) {
        // If user provided an invalid code, show error
        if (sanitizedData.referralCode.trim()) {
          setError(
            "Invalid referral ID. Please double-check the code or continue without referral ID."
          );
          setLoading(false);
          return;
        }
        // If default code is invalid (shouldn't happen), log error but continue
        console.error("Default referral ID is invalid:", DEFAULT_REFERRAL_ID);
      } else {
        referredBy = referralValidation.referrerData.id;
        referrerData = referralValidation.referrerData;
      }

      // Check if email already exists
      const emailQuery = query(
        collection(db, "users"),
        where("email", "==", sanitizedData.email.toLowerCase())
      );
      const emailSnapshot = await getDocs(emailQuery);
      
      if (!emailSnapshot.empty) {
        setError("This email is already registered. Please sign in instead.");
        setLoading(false);
        return;
      }

      // Save user data to Firestore with new cashback/commission fields
      const userData = {
        uid: user.uid,
        email: sanitizedData.email,
        name: sanitizedData.fullName,
        phoneNumber: sanitizedData.phone,
        address: sanitizedData.address,
        pincode: sanitizedData.pincode,
        referralIdUsed: usedReferralCode || "",
        myReferralId: myReferralId,
        referredBy: referredBy,
        createdAt: new Date().toISOString(),
        photoURL: user.photoURL || "",
        totalReferrals: 0,

        // EXISTING registration fee structure (keep for backward compatibility)
        registrationFee: {
          amount: 499,
          paid: false,
          paidAt: null,
          confirmedByAdmin: false,
          confirmedAt: null,
        },

        // Add cashback and commission fields
        cashbackBalance: 0, // Will be set to 499 when first package is confirmed
        cashbackEarned: 0, // Total cashback earned lifetime
        cashbackUsed: 0, // Total cashback used lifetime

        commissionBalance: 0, // Current available commission earnings
        commissionEarned: 0, // Total commission earned lifetime
        commissionUsed: 0, // Total commission used lifetime

        // Add package purchase flag
        hasPurchasedPackage: false,

        updatedAt: new Date().toISOString(),
      };

      await setDoc(doc(db, "users", user.uid), userData);

      // If referred by someone, update their referral count
      if (referrerData) {
        const referrerRef = doc(db, "users", referrerData.id);
        const referrerDoc = await getDoc(referrerRef);
        if (referrerDoc.exists()) {
          const currentCount = referrerDoc.data().totalReferrals || 0;
          await setDoc(
            referrerRef,
            { totalReferrals: currentCount + 1 },
            { merge: true }
          );
        }

        // Create referral tracking record
        const referralRecord = {
          referrerId: referrerData.id,
          referrerName: referrerData.name,
          referrerEmail: referrerData.email,
          referredUserId: user.uid,
          referredUserName: formData.fullName,
          referredUserEmail: user.email,
          referralCode: formData.referralCode.toUpperCase(),
          createdAt: new Date().toISOString(),
        };

        await setDoc(doc(collection(db, "referrals")), referralRecord);
      }

      setSignupComplete(true);
      setUserExists(true);
      setCurrentFlow("home");

      // NEW: Customize success message
      if (referrerData) {
        setSuccess(
          `🎉 Welcome aboard! You've successfully joined via ${referrerData.name}'s referral!`
        );
      } else {
        setSuccess("Account created successfully! Welcome aboard!");
      }
    } catch (error) {
      setError("Failed to create account: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle skip referral
  const handleSkipReferral = () => {
    setFormData((prev) => ({ ...prev, referralCode: "" }));
    handleFinalSignup();
  };

  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut(auth);
      // Clear all state
      setUser(null);
      setUserExists(false);
      setSignupComplete(false);
      setActiveStep(0);
      setCurrentFlow("public");
      setFormData({ fullName: "", email: "", phone: "", address: "", pincode: "", referralCode: "" });
      setValidationErrors({});
      setError("");
      setSuccess("");
      setReferrerInfo(null); // Clear referrer info
      setUrlReferralId(""); // Clear referral ID

      // Clear URL parameters to prevent referral loop after sign out
      const url = new URL(window.location);
      url.searchParams.delete("ref");
      url.searchParams.delete("auth_intent");
      url.searchParams.delete("item_id");
      window.history.replaceState({}, document.title, url.pathname);

      // Force page refresh to clear any cached state
      window.location.reload();
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // Handle back to public page
  const handleBackToPublic = () => {
    // Clear URL parameters to prevent referral loop
    const url = new URL(window.location);
    url.searchParams.delete("ref");
    window.history.replaceState({}, document.title, url.pathname);

    // Clear referral state
    setUrlReferralId("");
    setReferrerInfo(null);
    setFormData((prev) => ({ ...prev, referralCode: "" }));

    setCurrentFlow("public");
    setActiveStep(0);
    setError("");
    setSuccess("");
  };

  // Handle back to landing
  const handleBackToLanding = () => {
    setCurrentFlow("landing");
    setActiveStep(0);
    setError("");
    setSuccess("");
  };

  // NEW: Handle continue to home for existing users - REMOVED since we're only showing browse products
  // const handleContinueToHome = () => {
  //   setCurrentFlow("home");
  //   setError("");
  //   setSuccess("");
  // };

  // Handle admin panel navigation
  const handleAdminPanel = () => {
    setCurrentFlow("admin");
  };

  // Handle back to home from admin
  const handleBackToHome = () => {
    setCurrentFlow("home");
  };

  // Custom step icons
  const StepIcon = (props) => {
    const { active, completed } = props;
    const icons = {
      1: <GoogleIcon fontSize="small" />,
      2: <AccountCircle fontSize="small" />,
      3: <CardGiftcard fontSize="small" />,
    };

    return (
      <CustomStepIcon ownerState={{ active, completed }}>
        {completed ? <Check fontSize="small" /> : icons[String(props.icon)]}
      </CustomStepIcon>
    );
  };

  // Show loading spinner while checking auth state
  if (authLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, 
            ${primaryBlue[50]} 0%, 
            ${primaryBlue[100]} 25%, 
            ${primaryBlue[200]} 50%, 
            ${primaryBlue[100]} 75%, 
            ${primaryBlue[50]} 100%)`,
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 50%, rgba(0, 114, 206, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(11, 28, 72, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 40% 80%, rgba(0, 34, 102, 0.1) 0%, transparent 50%)
            `,
            zIndex: 0,
          },
        }}
      >
        <Box
          sx={{
            zIndex: 1,
            position: "relative",
            ...glassStyles,
            p: 4,
            textAlign: "center",
          }}
        >
          <CircularProgress size={60} sx={{ color: primaryBlue[500] }} />
          <Typography
            variant="h6"
            sx={{
              mt: 2,
              fontWeight: 600,
              color: primaryBlue[700],
            }}
          >
            Loading...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Show public home page by default (no auth required)
  if (currentFlow === "public") {
    return (
      <PublicHomePage
        user={user}
        onAuthRequired={handleAuthRequired}
        onSignOut={handleSignOut}
      />
    );
  }

  // Show authenticated home page if user exists and is signed in
  if (user && userExists && signupComplete && currentFlow === "home") {
    return (
      <HomePage
        user={user}
        onSignOut={handleSignOut}
        onAdminPanel={handleAdminPanel}
      />
    );
  }

  // Show admin panel if requested
  if (currentFlow === "admin" && user && userExists) {
    return (
      <AdminGuard user={user} onBackToHome={handleBackToHome}>
        <AdminDashboard
          user={user}
          onSignOut={handleSignOut}
          onBackToHome={handleBackToHome}
        />
      </AdminGuard>
    );
  }

  // NEW: Show existing user referral message
  if (currentFlow === "existing-user-referral" && user && userExists) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, 
            ${primaryBlue[50]} 0%, 
            ${primaryBlue[100]} 25%, 
            ${primaryBlue[200]} 50%, 
            ${primaryBlue[100]} 75%, 
            ${primaryBlue[50]} 100%)`,
          position: "relative",
          py: 4,
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 50%, rgba(0, 114, 206, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(11, 28, 72, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 40% 80%, rgba(0, 34, 102, 0.1) 0%, transparent 50%)
            `,
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
          <Paper
            elevation={0}
            sx={{
              ...glassStyles,
              p: 4,
              bgcolor: "rgba(255, 255, 255, 0.5)",
              textAlign: "center",
            }}
          >
            {/* Show referrer info if available */}
            {referrerInfo && (
              <Card
                variant="outlined"
                sx={{
                  mb: 3,
                  bgcolor: "rgba(0, 114, 206, 0.1)",
                  borderColor: primaryBlue[300],
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                    }}
                  >
                    <Avatar
                      src={referrerInfo.photoURL}
                      sx={{
                        width: 60,
                        height: 60,
                        mr: 2,
                        border: `3px solid ${primaryBlue[300]}`,
                      }}
                    >
                      <Person sx={{ fontSize: 30 }} />
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{ color: primaryBlue[700], fontWeight: 600 }}
                      >
                        {referrerInfo.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        wants to refer you!
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={`Referral ID: ${referrerInfo.myReferralId}`}
                    color="primary"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
            )}

            <Avatar
              sx={{
                width: 80,
                height: 80,
                mx: "auto",
                mb: 2,
                bgcolor: "warning.main",
                boxShadow: "0 4px 20px rgba(255, 152, 0, 0.4)",
              }}
            >
              <Person sx={{ width: 40, height: 40 }} />
            </Avatar>

            <Typography
              variant="h4"
              align="center"
              gutterBottom
              sx={{
                fontWeight: "bold",
                background: `linear-gradient(45deg, ${primaryBlue[700]}, ${primaryBlue[500]})`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Already a Member!
            </Typography>

            {error && (
              <Alert
                severity="info"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  ...glassStyles,
                  bgcolor: "rgba(0, 114, 206, 0.1)",
                  color: "info.dark",
                  border: "1px solid rgba(0, 114, 206, 0.3)",
                  "& .MuiAlert-icon": {
                    color: "info.main",
                  },
                }}
              >
                {error}
              </Alert>
            )}

            <Box sx={{ mt: 4, textAlign: "center" }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleBackToPublic}
                sx={{
                  py: 2,
                  px: 4,
                  background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[500]})`,
                  borderRadius: "12px",
                  fontWeight: "bold",
                  boxShadow: "0 8px 20px rgba(0, 114, 206, 0.3)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  "&:hover": {
                    background: `linear-gradient(45deg, ${primaryBlue[700]}, ${primaryBlue[600]})`,
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 25px rgba(0, 114, 206, 0.4)",
                  },
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                Browse Products
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  // NEW: Show referral signup page for non-authenticated users with referral links
  if (currentFlow === "referral-signup" && !user) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, 
            ${primaryBlue[50]} 0%, 
            ${primaryBlue[100]} 25%, 
            ${primaryBlue[200]} 50%, 
            ${primaryBlue[100]} 75%, 
            ${primaryBlue[50]} 100%)`,
          position: "relative",
          py: 4,
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 50%, rgba(0, 114, 206, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(11, 28, 72, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 40% 80%, rgba(0, 34, 102, 0.1) 0%, transparent 50%)
            `,
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
          <Paper
            elevation={0}
            sx={{
              ...glassStyles,
              p: 4,
              bgcolor: "rgba(255, 255, 255, 0.5)",
            }}
          >
            {/* Back button */}
            <Box sx={{ mb: 3 }}>
              <Button
                onClick={handleBackToPublic}
                startIcon={<ArrowBack />}
                sx={{
                  color: primaryBlue[600],
                  "&:hover": {
                    bgcolor: "rgba(0, 114, 206, 0.1)",
                  },
                }}
              >
                Continue browsing without signing up
              </Button>
            </Box>

            {/* Referrer Information Section */}
            {referrerLoading ? (
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <CircularProgress size={40} />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Loading referrer information...
                </Typography>
              </Box>
            ) : referrerInfo ? (
              <Card
                variant="outlined"
                sx={{
                  mb: 4,
                  bgcolor: "rgba(76, 175, 80, 0.1)",
                  borderColor: "success.main",
                  border: "2px solid",
                }}
              >
                <CardContent sx={{ textAlign: "center" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                    }}
                  >
                    <Handshake
                      sx={{ color: "success.main", mr: 1, fontSize: 32 }}
                    />
                    <Typography
                      variant="h6"
                      sx={{ color: "success.main", fontWeight: 600 }}
                    >
                      You're Invited!
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                    }}
                  >
                    <Avatar
                      src={referrerInfo.photoURL}
                      sx={{
                        width: 70,
                        height: 70,
                        mr: 2,
                        border: `3px solid ${theme.palette.success.main}`,
                      }}
                    >
                      <Person sx={{ fontSize: 35 }} />
                    </Avatar>
                    <Box sx={{ textAlign: "left" }}>
                      <Typography
                        variant="h5"
                        sx={{ color: "success.dark", fontWeight: 700 }}
                      >
                        {referrerInfo.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        wants you to join InfinityWhale
                      </Typography>
                      <Chip
                        label={`Referral ID: ${referrerInfo.myReferralId}`}
                        color="success"
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  </Box>

                  <Alert severity="success" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      🎉 Join through {referrerInfo.name}'s referral and become
                      part of our community!
                    </Typography>
                  </Alert>
                </CardContent>
              </Card>
            ) : (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  ⚠️ Invalid referral link. The referral ID could not be found.
                </Typography>
              </Alert>
            )}

            <Box sx={{ textAlign: "center" }}>
              <Avatar
                sx={{
                  width: 70,
                  height: 70,
                  mx: "auto",
                  mb: 2,
                  background: `linear-gradient(135deg, ${primaryBlue[500]}, ${primaryBlue[700]})`,
                  boxShadow: "0 4px 20px rgba(0, 114, 206, 0.4)",
                }}
              >
                <PersonAdd sx={{ width: 32, height: 32 }} />
              </Avatar>

              <Typography
                variant="h4"
                align="center"
                gutterBottom
                sx={{
                  fontWeight: "bold",
                  background: `linear-gradient(45deg, ${primaryBlue[700]}, ${primaryBlue[500]})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Join InfinityWhale
              </Typography>

              <Typography
                variant="body1"
                align="center"
                sx={{ color: primaryBlue[700], mb: 3 }}
              >
                {referrerInfo
                  ? `Complete your signup to join via ${referrerInfo.name}'s referral`
                  : "Sign up to access premium ecommerce products"}
              </Typography>
            </Box>

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  ...glassStyles,
                  bgcolor: "rgba(244, 67, 54, 0.1)",
                  color: "error.dark",
                  border: "1px solid rgba(244, 67, 54, 0.3)",
                  "& .MuiAlert-icon": {
                    color: "error.main",
                  },
                }}
              >
                {error}
              </Alert>
            )}

            {success && (
              <Alert
                severity="success"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  ...glassStyles,
                  bgcolor: "rgba(76, 175, 80, 0.1)",
                  color: "success.dark",
                  border: "1px solid rgba(76, 175, 80, 0.3)",
                  "& .MuiAlert-icon": {
                    color: "success.main",
                  },
                }}
              >
                {success}
              </Alert>
            )}

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<GoogleIcon />}
                onClick={() => handleGoogleAuth("signup")}
                disabled={loading || !referrerInfo}
                sx={{
                  py: 2,
                  background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[500]})`,
                  borderRadius: "12px",
                  fontWeight: "bold",
                  boxShadow: "0 8px 20px rgba(0, 114, 206, 0.3)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  "&:hover": {
                    background: `linear-gradient(45deg, ${primaryBlue[700]}, ${primaryBlue[600]})`,
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 25px rgba(0, 114, 206, 0.4)",
                  },
                  "&:disabled": {
                    background: "rgba(0, 0, 0, 0.12)",
                    color: "rgba(0, 0, 0, 0.26)",
                  },
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : referrerInfo ? (
                  `Join via ${referrerInfo.name}'s Referral`
                ) : (
                  "Invalid Referral Link"
                )}
              </Button>
            </Box>

            {referrerInfo && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: "rgba(0, 114, 206, 0.05)",
                  borderRadius: 2,
                  border: `1px solid ${primaryBlue[100]}`,
                }}
              >
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ color: primaryBlue[700], fontWeight: 500 }}
                >
                  🔒 Secure signup with Google • No spam, ever • Free to join
                </Typography>
              </Box>
            )}
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg, 
          ${primaryBlue[50]} 0%, 
          ${primaryBlue[100]} 25%, 
          ${primaryBlue[200]} 50%, 
          ${primaryBlue[100]} 75%, 
          ${primaryBlue[50]} 100%)`,
        position: "relative",
        py: 4,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 50%, rgba(0, 114, 206, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(11, 28, 72, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(0, 34, 102, 0.1) 0%, transparent 50%)
          `,
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            ...glassStyles,
            p: 4,
            bgcolor: "rgba(255, 255, 255, 0.5)",
          }}
        >
          {/* Landing Page - Choose Sign Up or Sign In */}
          {currentFlow === "landing" && (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <Avatar
                  sx={{
                    width: 70,
                    height: 70,
                    mb: 2,
                    background: `linear-gradient(135deg, ${primaryBlue[500]}, ${primaryBlue[700]})`,
                    boxShadow: "0 4px 20px rgba(0, 114, 206, 0.4)",
                  }}
                >
                  <GoogleIcon sx={{ width: 32, height: 32 }} />
                </Avatar>
                <Typography
                  variant="h4"
                  align="center"
                  gutterBottom
                  sx={{
                    fontWeight: "bold",
                    background: `linear-gradient(45deg, ${primaryBlue[700]}, ${primaryBlue[500]})`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Sign In to Continue
                </Typography>
                <Typography
                  variant="body1"
                  align="center"
                  sx={{ color: primaryBlue[700] }}
                >
                  Please sign in to purchase packages and access all features
                </Typography>
              </Box>

              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    ...glassStyles,
                    bgcolor: "rgba(244, 67, 54, 0.1)",
                    color: "error.dark",
                    border: "1px solid rgba(244, 67, 54, 0.3)",
                    "& .MuiAlert-icon": {
                      color: "error.main",
                    },
                  }}
                >
                  {error}
                </Alert>
              )}

              {success && (
                <Alert
                  severity="success"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    ...glassStyles,
                    bgcolor: "rgba(76, 175, 80, 0.1)",
                    color: "success.dark",
                    border: "1px solid rgba(76, 175, 80, 0.3)",
                    "& .MuiAlert-icon": {
                      color: "success.main",
                    },
                  }}
                >
                  {success}
                </Alert>
              )}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  mt: 2,
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<GoogleIcon />}
                  onClick={() => handleGoogleAuth("signup")}
                  disabled={loading}
                  sx={{
                    py: 2,
                    background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[500]})`,
                    borderRadius: "12px",
                    fontWeight: "bold",
                    boxShadow: "0 8px 20px rgba(0, 114, 206, 0.3)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    "&:hover": {
                      background: `linear-gradient(45deg, ${primaryBlue[700]}, ${primaryBlue[600]})`,
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 25px rgba(0, 114, 206, 0.4)",
                    },
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "white" }} />
                  ) : (
                    "Sign Up with Google"
                  )}
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<GoogleIcon />}
                  onClick={() => handleGoogleAuth("signin")}
                  disabled={loading}
                  sx={{
                    py: 2,
                    borderColor: primaryBlue[300],
                    color: primaryBlue[600],
                    fontWeight: 600,
                    bgcolor: "rgba(255, 255, 255, 0.7)",
                    backdropFilter: "blur(10px)",
                    borderRadius: "12px",
                    "&:hover": {
                      borderColor: primaryBlue[500],
                      bgcolor: "rgba(255, 255, 255, 0.9)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  {loading ? (
                    <CircularProgress
                      size={24}
                      sx={{ color: primaryBlue[500] }}
                    />
                  ) : (
                    "Sign In with Google"
                  )}
                </Button>
              </Box>

              <Box
                sx={{
                  mt: 4,
                  p: 2,
                  bgcolor: "rgba(0, 114, 206, 0.05)",
                  borderRadius: 2,
                  border: `1px solid ${primaryBlue[100]}`,
                }}
              >
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ color: primaryBlue[700], fontWeight: 500 }}
                >
                  We'll automatically detect if you're a new or returning user
                </Typography>
              </Box>

              <Box sx={{ mt: 4, textAlign: "center" }}>
                <Button
                  variant="text"
                  onClick={handleBackToPublic}
                  startIcon={<ArrowBack />}
                  sx={{
                    color: primaryBlue[600],
                    "&:hover": {
                      bgcolor: "rgba(0, 114, 206, 0.1)",
                    },
                  }}
                >
                  Continue browsing without signing in
                </Button>
              </Box>
            </>
          )}

          {/* Sign Up Flow */}
          {currentFlow === "signup" && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 3,
                }}
              >
                <Button
                  onClick={handleBackToLanding}
                  startIcon={<ArrowBack />}
                  sx={{
                    color: primaryBlue[600],
                    "&:hover": {
                      bgcolor: "rgba(0, 114, 206, 0.1)",
                    },
                  }}
                >
                  Back
                </Button>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "bold",
                    background: `linear-gradient(45deg, ${primaryBlue[700]}, ${primaryBlue[500]})`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Sign Up
                </Typography>
                <Box sx={{ width: 64 }} /> {/* Spacer for alignment */}
              </Box>

              {/* NEW: Show referrer info in signup flow if available */}
              {referrerInfo && (
                <Card
                  variant="outlined"
                  sx={{
                    mb: 3,
                    bgcolor: "rgba(76, 175, 80, 0.1)",
                    borderColor: "success.main",
                  }}
                >
                  <CardContent sx={{ py: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Avatar
                        src={referrerInfo.photoURL}
                        sx={{
                          width: 40,
                          height: 40,
                          mr: 2,
                          border: `2px solid ${theme.palette.success.main}`,
                        }}
                      >
                        <Person />
                      </Avatar>
                      <Box sx={{ textAlign: "left" }}>
                        <Typography
                          variant="body2"
                          sx={{ color: "success.dark", fontWeight: 600 }}
                        >
                          Joining via {referrerInfo.name}'s referral
                        </Typography>
                        <Chip
                          label={referrerInfo.myReferralId}
                          color="success"
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Show completion message */}
              {signupComplete ? (
                <Box
                  textAlign="center"
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    bgcolor: "rgba(76, 175, 80, 0.1)",
                    border: "1px solid rgba(76, 175, 80, 0.3)",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 70,
                      height: 70,
                      mx: "auto",
                      mb: 2,
                      bgcolor: "success.main",
                      boxShadow: "0 4px 20px rgba(76, 175, 80, 0.4)",
                    }}
                  >
                    <Check sx={{ width: 40, height: 40 }} />
                  </Avatar>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: "bold",
                      color: "success.main",
                      mb: 2,
                    }}
                    gutterBottom
                  >
                    🎉 Welcome Aboard!
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mb: 3, color: primaryBlue[700], fontWeight: 500 }}
                  >
                    Your account has been created successfully!
                  </Typography>

                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => setCurrentFlow("home")}
                    sx={{
                      py: 1.5,
                      px: 4,
                      mt: 2,
                      background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[500]})`,
                      borderRadius: "12px",
                      fontWeight: "bold",
                      boxShadow: "0 8px 20px rgba(0, 114, 206, 0.3)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      "&:hover": {
                        background: `linear-gradient(45deg, ${primaryBlue[700]}, ${primaryBlue[600]})`,
                        transform: "translateY(-2px)",
                        boxShadow: "0 12px 25px rgba(0, 114, 206, 0.4)",
                      },
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    Go to Dashboard
                  </Button>
                </Box>
              ) : (
                <>
                  {/* Stepper - Only show if not coming from referral link */}
                  {!urlReferralId && (
                    <Stepper
                      activeStep={activeStep}
                      alternativeLabel={!isMobile}
                      orientation={isMobile ? "vertical" : "horizontal"}
                      connector={<CustomConnector />}
                      sx={{ mb: 4 }}
                    >
                      {steps.map((label, index) => (
                        <Step key={label}>
                          <StepLabel StepIconComponent={StepIcon}>
                            {label}
                          </StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  )}

                  {/* Error Alert */}
                  {error && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: 3,
                        borderRadius: 2,
                        ...glassStyles,
                        bgcolor: "rgba(244, 67, 54, 0.1)",
                        color: "error.dark",
                        border: "1px solid rgba(244, 67, 54, 0.3)",
                        "& .MuiAlert-icon": {
                          color: "error.main",
                        },
                      }}
                    >
                      {error}
                    </Alert>
                  )}

                  {/* Success Alert */}
                  {success && !signupComplete && (
                    <Alert
                      severity="success"
                      sx={{
                        mb: 3,
                        borderRadius: 2,
                        ...glassStyles,
                        bgcolor: "rgba(76, 175, 80, 0.1)",
                        color: "success.dark",
                        border: "1px solid rgba(76, 175, 80, 0.3)",
                        "& .MuiAlert-icon": {
                          color: "success.main",
                        },
                      }}
                    >
                      {success}
                    </Alert>
                  )}

                  {/* Step 1: Google Sign-in */}
                  {activeStep === 0 && (
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 3,
                        borderRadius: 2,
                        bgcolor: "rgba(255, 255, 255, 0.3)",
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 60,
                          height: 60,
                          mx: "auto",
                          mb: 2,
                          background: `linear-gradient(135deg, ${primaryBlue[500]}, ${primaryBlue[700]})`,
                          boxShadow: "0 4px 20px rgba(0, 114, 206, 0.4)",
                        }}
                      >
                        <GoogleIcon sx={{ width: 30, height: 30 }} />
                      </Avatar>
                      <Typography
                        variant="h5"
                        sx={{
                          mb: 2,
                          fontWeight: 600,
                          color: primaryBlue[700],
                        }}
                      >
                        Sign In with Google
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ mb: 3, color: primaryBlue[600] }}
                      >
                        Click the button below to continue with Google
                        authentication
                      </Typography>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<GoogleIcon />}
                        onClick={() => handleGoogleAuth("signup")}
                        disabled={loading}
                        sx={{
                          px: 4,
                          py: 1.5,
                          background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[500]})`,
                          borderRadius: "12px",
                          fontWeight: "bold",
                          boxShadow: "0 8px 20px rgba(0, 114, 206, 0.3)",
                          border: "1px solid rgba(255, 255, 255, 0.3)",
                          "&:hover": {
                            background: `linear-gradient(45deg, ${primaryBlue[700]}, ${primaryBlue[600]})`,
                            transform: "translateY(-2px)",
                            boxShadow: "0 12px 25px rgba(0, 114, 206, 0.4)",
                          },
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={24} sx={{ color: "white" }} />
                        ) : (
                          "Continue with Google"
                        )}
                      </Button>
                    </Box>
                  )}

                  {/* Step 2: Personal Information */}
                  {activeStep === 1 && (
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        bgcolor: "rgba(255, 255, 255, 0.3)",
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 3,
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: primaryBlue[500],
                            mr: 2,
                            boxShadow: "0 4px 10px rgba(0, 114, 206, 0.3)",
                          }}
                        >
                          <AccountCircle />
                        </Avatar>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            color: primaryBlue[700],
                          }}
                        >
                          Personal Information
                        </Typography>
                      </Box>

                      <TextField
                        fullWidth
                        label="Full Name"
                        value={formData.fullName}
                        onChange={handleInputChange("fullName")}
                        error={!!validationErrors.fullName}
                        helperText={validationErrors.fullName}
                        margin="normal"
                        required
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <AccountCircle
                              sx={{ color: primaryBlue[400], mr: 1 }}
                            />
                          ),
                        }}
                        sx={{
                          mb: 2,
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "rgba(255, 255, 255, 0.7)",
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: primaryBlue[400],
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: primaryBlue[500],
                            },
                          },
                        }}
                      />
                      <TextField
                        fullWidth
                        label="Phone Number"
                        value={formData.phone}
                        onChange={handleInputChange("phone")}
                        error={!!validationErrors.phone}
                        helperText={validationErrors.phone}
                        margin="normal"
                        required
                        placeholder="+1 (555) 123-4567"
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <Phone sx={{ color: primaryBlue[400], mr: 1 }} />
                          ),
                        }}
                        sx={{
                          mb: 3,
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "rgba(255, 255, 255, 0.7)",
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: primaryBlue[400],
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: primaryBlue[500],
                            },
                          },
                        }}
                      />
                      
                      <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        value={formData.email || user?.email || ""}
                        onChange={handleInputChange("email")}
                        margin="normal"
                        required
                        error={!!validationErrors.email}
                        helperText={validationErrors.email}
                        variant="outlined"
                        sx={{
                          mb: 2,
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "rgba(255, 255, 255, 0.7)",
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: primaryBlue[400],
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: primaryBlue[500],
                            },
                          },
                        }}
                      />
                      
                      <TextField
                        fullWidth
                        label="Address"
                        value={formData.address}
                        onChange={handleInputChange("address")}
                        margin="normal"
                        required
                        multiline
                        rows={2}
                        error={!!validationErrors.address}
                        helperText={validationErrors.address}
                        variant="outlined"
                        sx={{
                          mb: 2,
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "rgba(255, 255, 255, 0.7)",
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: primaryBlue[400],
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: primaryBlue[500],
                            },
                          },
                        }}
                      />
                      
                      <TextField
                        fullWidth
                        label="Pincode"
                        value={formData.pincode}
                        onChange={handleInputChange("pincode")}
                        margin="normal"
                        required
                        inputProps={{ maxLength: 6 }}
                        error={!!validationErrors.pincode}
                        helperText={validationErrors.pincode}
                        variant="outlined"
                        sx={{
                          mb: 3,
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "rgba(255, 255, 255, 0.7)",
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: primaryBlue[400],
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: primaryBlue[500],
                            },
                          },
                        }}
                      />
                      
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handlePersonalInfoSubmit}
                        sx={{
                          py: 1.5,
                          background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[500]})`,
                          borderRadius: "12px",
                          fontWeight: "bold",
                          boxShadow: "0 8px 20px rgba(0, 114, 206, 0.3)",
                          border: "1px solid rgba(255, 255, 255, 0.3)",
                          "&:hover": {
                            background: `linear-gradient(45deg, ${primaryBlue[700]}, ${primaryBlue[600]})`,
                            transform: "translateY(-2px)",
                            boxShadow: "0 12px 25px rgba(0, 114, 206, 0.4)",
                          },
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      >
                        {urlReferralId ? "Complete Signup" : "Continue"}
                      </Button>
                    </Box>
                  )}

                  {/* Step 3: Referral Code - Only show if no URL referral */}
                  {activeStep === 2 && !urlReferralId && (
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        bgcolor: "rgba(255, 255, 255, 0.3)",
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 3,
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: primaryBlue[500],
                            mr: 2,
                            boxShadow: "0 4px 10px rgba(0, 114, 206, 0.3)",
                          }}
                        >
                          <CardGiftcard />
                        </Avatar>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            color: primaryBlue[700],
                          }}
                        >
                          Referral Code
                        </Typography>
                      </Box>

                      <Typography
                        variant="body1"
                        sx={{
                          mb: 2,
                          color: primaryBlue[600],
                          bgcolor: "rgba(0, 114, 206, 0.05)",
                          p: 2,
                          borderRadius: 2,
                          border: `1px solid ${primaryBlue[100]}`,
                        }}
                      >
                        Did someone refer you? Enter their referral ID below to
                        get started!
                      </Typography>

                      <TextField
                        fullWidth
                        label="Referral ID (Optional)"
                        value={formData.referralCode}
                        onChange={handleInputChange("referralCode")}
                        error={!!validationErrors.referralCode}
                        helperText={validationErrors.referralCode}
                        margin="normal"
                        placeholder="Enter 8-character referral ID"
                        inputProps={{ style: { textTransform: "uppercase" } }}
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <CardGiftcard
                              sx={{ color: primaryBlue[400], mr: 1 }}
                            />
                          ),
                        }}
                        sx={{
                          mb: 4,
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "rgba(255, 255, 255, 0.7)",
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: primaryBlue[400],
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: primaryBlue[500],
                            },
                          },
                        }}
                      />

                      <Box sx={{ display: "flex", gap: 2 }}>
                        <Button
                          variant="outlined"
                          onClick={handleSkipReferral}
                          disabled={loading}
                          sx={{
                            flex: 1,
                            py: 1.5,
                            borderColor: primaryBlue[300],
                            color: primaryBlue[600],
                            fontWeight: 600,
                            bgcolor: "rgba(255, 255, 255, 0.7)",
                            backdropFilter: "blur(10px)",
                            borderRadius: "12px",
                            "&:hover": {
                              borderColor: primaryBlue[500],
                              bgcolor: "rgba(255, 255, 255, 0.9)",
                              transform: "translateY(-2px)",
                            },
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          }}
                        >
                          Skip
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleFinalSignup}
                          disabled={loading}
                          sx={{
                            flex: 1,
                            py: 1.5,
                            background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[500]})`,
                            borderRadius: "12px",
                            fontWeight: "bold",
                            boxShadow: "0 8px 20px rgba(0, 114, 206, 0.3)",
                            border: "1px solid rgba(255, 255, 255, 0.3)",
                            "&:hover": {
                              background: `linear-gradient(45deg, ${primaryBlue[700]}, ${primaryBlue[600]})`,
                              transform: "translateY(-2px)",
                              boxShadow: "0 12px 25px rgba(0, 114, 206, 0.4)",
                            },
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          }}
                        >
                          {loading ? (
                            <CircularProgress
                              size={24}
                              sx={{ color: "white" }}
                            />
                          ) : (
                            "Complete Signup"
                          )}
                        </Button>
                      </Box>

                      <Divider
                        sx={{
                          my: 3,
                          "&::before, &::after": {
                            borderColor: "rgba(0, 114, 206, 0.2)",
                          },
                        }}
                      >
                        <Chip
                          label="OR"
                          sx={{
                            bgcolor: "rgba(0, 114, 206, 0.1)",
                            color: primaryBlue[600],
                            fontWeight: 600,
                            border: `1px solid ${primaryBlue[200]}`,
                          }}
                        />
                      </Divider>

                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "rgba(255, 255, 255, 0.5)",
                          borderRadius: 2,
                          textAlign: "center",
                          border: `1px dashed ${primaryBlue[200]}`,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: primaryBlue[700], fontWeight: 500 }}
                        >
                          Don't have a referral code? No problem! You can still
                          sign up and enjoy all our features.
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export {
  addFirstTimeCashback,
  updateCommissionBalance,
  updateCashbackBalance,
  calculateAvailableDiscounts,
};

export default SignupPage;
